'use client';

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Link from 'next/link';

const HeroSection: React.FC = () => {
  const [currentRoleIndex, setCurrentRoleIndex] = useState(0);
  const [mounted, setMounted] = useState(false);

  const aiRoles = [
    'Personal Assistant',
    'Life Companion',
    'Daily Helper',
    'Voice Assistant',
    'Smart Guide',
    'Companion',
    'Personal Coach'
  ];

  useEffect(() => {
    // Mark as mounted to enable text cycling
    setMounted(true);
  }, []);

  useEffect(() => {
    // Only start cycling after component is mounted to avoid blocking initial render
    if (!mounted) return;

    const interval = setInterval(() => {
      setCurrentRoleIndex((prev) => (prev + 1) % aiRoles.length);
    }, 2500); // Change every 2.5 seconds

    return () => clearInterval(interval);
  }, [mounted, aiRoles.length]);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden px-3 sm:px-4 md:px-6 lg:px-8 pt-16 sm:pt-20 md:pt-24">
      {/* Hero Content */}
      <div className="relative z-10 text-center max-w-4xl mx-auto w-full">
        {/* CRITICAL: No Framer Motion on LCP element - instant render */}
        <h1 className="text-2xl xs:text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold mb-4 sm:mb-6 leading-tight">
          <div className="flex items-center justify-center flex-col sm:flex-row gap-2 sm:gap-4">
            <span className="text-white drop-shadow-2xl">
              AI
            </span>
            <div className="relative inline-block min-w-[180px] xs:min-w-[200px] sm:min-w-[280px] md:min-w-[350px] lg:min-w-[400px]">
              {/* Simple CSS transition instead of AnimatePresence for instant render */}
              <span
                key={currentRoleIndex}
                className="block bg-gradient-to-r from-orange-400 to-pink-400 bg-clip-text text-transparent drop-shadow-2xl text-center transition-opacity duration-500"
                style={{
                  animation: mounted ? 'fadeIn 0.5s ease-in' : 'none'
                }}
              >
                {aiRoles[currentRoleIndex]}
              </span>
            </div>
          </div>
          <div className="text-white drop-shadow-2xl mt-2 text-lg xs:text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl">
            for Life Enhancement
          </div>
        </h1>

        <p className="text-sm xs:text-base sm:text-lg md:text-xl text-gray-300 mb-6 sm:mb-8 max-w-2xl mx-auto font-normal leading-relaxed px-2">
          Managing your daily tasks, organizing your schedule, and providing intelligent assistance,
          so your personal life runs smoother with less stress
        </p>

        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center">
          {/* <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="w-full sm:w-auto"
          >
            <Link href="/waitlist" className="block w-full sm:w-auto bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 px-6 sm:px-8 py-3 sm:py-4 rounded-xl sm:rounded-2xl text-white font-semibold text-base sm:text-lg transition-all shadow-lg hover:shadow-purple-500/50 text-center">
              Join Waitlist
            </Link>
          </motion.div> */}

          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="w-full sm:w-auto"
          >
            <Link href="/voice-chat" className="block w-full sm:w-auto border-2 border-white/20 hover:border-white/40 px-6 sm:px-8 py-3 sm:py-4 rounded-xl sm:rounded-2xl text-white font-semibold text-base sm:text-lg transition-all hover:bg-white/5 backdrop-blur-md text-center">
              Try AI Now
            </Link>
          </motion.div>
        </div>

      </div>

      {/* Scroll Indicator - Hidden on mobile for space */}
      <motion.div
        className="absolute bottom-8 sm:bottom-12 left-1/2 transform -translate-x-1/2 text-white/40 text-sm sm:text-base md:text-lg font-light tracking-widest z-10 hidden sm:block"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 1.2 }}
      >
        <motion.div
          animate={{
            y: [0, 6, 0],
            opacity: [0.4, 0.8, 0.4]
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="flex flex-col items-center"
        >
          <div className="mb-2">↓</div>
          <div className="text-xs sm:text-sm">DISCOVER MORE</div>
        </motion.div>
      </motion.div>

      {/* CSS keyframes for fade-in animation */}
      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
      `}</style>
    </section>
  );
};

export default HeroSection;